import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Users, Building2, Utensils, CreditCard, Ban, CheckCircle2, PauseCircle, PlayCircle, ChevronRight, Trash2, DatabaseBackup, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const PROTECTED_EMAILS = new Set(["info@kouzinahospitality.com", "info@wshpl.com"]);

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-6 border border-border shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
          <div className="font-heading font-extrabold text-4xl mt-3">{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-lg ${accent} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </Card>
  );
}

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Sidebar routes all render this component, but the active Tabs pane is
  // derived from the URL so the sidebar "Users" / "Restaurants" links actually
  // switch the visible table.
  const activeTab = location.pathname.startsWith("/admin/restaurants") ? "restaurants" : "users";
  const setTab = (v) => navigate(v === "restaurants" ? "/admin/restaurants" : "/admin/users");

  const loadAll = useCallback(async () => {
    try {
      const [s, u, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/restaurants"),
      ]);
      setStats(s.data); setUsers(u.data); setRestaurants(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleUser = async (u, action) => {
    try {
      await api.post(`/admin/users/${u.id}/${action}`);
      toast.success(`User ${action}ned`.replace("nn", "n"));
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleRestaurant = async (r, action) => {
    try {
      await api.post(`/admin/restaurants/${r.id}/${action}`);
      toast.success(`Restaurant ${action}ed`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (u) => {
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email} (${data.deleted_restaurants} outlets removed)`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cleanupTestAccounts = async () => {
    try {
      const { data } = await api.post("/admin/cleanup-test-accounts");
      toast.success(`Purged ${data.deleted_users} test account${data.deleted_users !== 1 ? "s" : ""} (${data.deleted_restaurants} outlets)`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">Platform control</div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Super admin overview</h1>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-full border-amber-500 text-amber-700 hover:bg-amber-500/10" data-testid="cleanup-db-btn">
              <DatabaseBackup className="w-4 h-4" /> Database cleanup
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="cleanup-db-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-600" /> Purge all test accounts?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>every owner account</strong> except the protected ones
                (<code className="font-mono text-xs">info@kouzinahospitality.com</code> &amp; <code className="font-mono text-xs">info@wshpl.com</code>) along with
                all their restaurants, menus, images, payments, and activity logs. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={cleanupTestAccounts} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="confirm-cleanup-db">
                Yes, purge accounts
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Active users" value={stats?.active_users ?? "—"} icon={Users} accent="bg-brand-navy" />
        <StatCard label="Restaurants" value={stats?.total_restaurants ?? "—"} icon={Building2} accent="bg-brand-orange" />
        <StatCard label="Menu items" value={stats?.total_items ?? "—"} icon={Utensils} accent="bg-brand-navy" />
        <StatCard label="Active subscriptions" value={stats?.active_subscriptions ?? "—"} icon={CreditCard} accent="bg-brand-orange" />
      </div>

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="restaurants" data-testid="tab-restaurants">Restaurants</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <Card className="mt-4 border border-border">
            <Table data-testid="admin-users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow
                    key={u.id}
                    className="cursor-pointer hover:bg-muted/60"
                    onClick={() => navigate(`/admin/users/${u.id}`)}
                    data-testid={`user-row-${u.id}`}
                  >
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {u.is_banned ? <Badge variant="destructive">Banned</Badge>
                       : u.is_suspended ? <Badge className="bg-amber-500 hover:bg-amber-500">Suspended</Badge>
                       : <Badge className="bg-veg hover:bg-veg">Active</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/admin/users/${u.id}`} onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" data-testid={`view-user-${u.id}`}>
                          Details <ChevronRight className="w-4 h-4" />
                        </Button>
                      </Link>
                      {u.is_banned ? (
                        <Button size="sm" variant="outline" onClick={() => toggleUser(u, "unban")} data-testid={`unban-${u.id}`}>
                          <CheckCircle2 className="w-4 h-4" /> Unban
                        </Button>
                      ) : (
                        <Button size="sm" variant="destructive" onClick={() => toggleUser(u, "ban")} data-testid={`ban-${u.id}`}>
                          <Ban className="w-4 h-4" /> Ban
                        </Button>
                      )}
                      {u.is_suspended ? (
                        <Button size="sm" variant="outline" onClick={() => toggleUser(u, "unsuspend")} data-testid={`unsuspend-${u.id}`}>
                          <PlayCircle className="w-4 h-4" /> Resume
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => toggleUser(u, "suspend")} data-testid={`suspend-${u.id}`}>
                          <PauseCircle className="w-4 h-4" /> Suspend
                        </Button>
                      )}
                      {!PROTECTED_EMAILS.has((u.email || "").toLowerCase()) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`delete-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Permanently delete {u.email}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                All of this owner&rsquo;s restaurants, menus, images, subscriptions, payments and activity logs
                                will be wiped. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteUser(u)} className="bg-destructive hover:bg-destructive/90 text-white" data-testid={`confirm-delete-${u.id}`}>
                                Delete account
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No users yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="restaurants">
          <Card className="mt-4 border border-border">
            <Table data-testid="admin-restaurants-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {restaurants.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.slug}</TableCell>
                    <TableCell>{r.item_count}</TableCell>
                    <TableCell>
                      {r.is_suspended ? <Badge className="bg-amber-500 hover:bg-amber-500">Suspended</Badge> : <Badge className="bg-veg hover:bg-veg">Live</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <a href={`/m/${r.slug}`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost" data-testid={`view-menu-${r.id}`}>View menu</Button>
                      </a>
                      {r.is_suspended ? (
                        <Button size="sm" variant="outline" onClick={() => toggleRestaurant(r, "unsuspend")} data-testid={`rest-resume-${r.id}`}>
                          <PlayCircle className="w-4 h-4" /> Resume
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => toggleRestaurant(r, "suspend")} data-testid={`rest-suspend-${r.id}`}>
                          <PauseCircle className="w-4 h-4" /> Suspend
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {restaurants.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No restaurants yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
